import { useState } from "react";
import { api } from "@/lib/api";
import { PROJECT_TYPES, projectTypeLabel } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Pencil, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin-only inline control that lets an administrator change a
 * project's `project_type` after creation. Rendered as a pencil icon
 * next to the type label in the project header; opens a dialog with
 * a Select of the same list used by the "New Project" form so the
 * value stays consistent across the app.
 *
 * On save, PATCHes /api/projects/{id} and calls `onSaved(updated)`
 * so the parent page can refresh its state without a full reload.
 * Photo templates auto-resolve on the next `_resolve_template` call
 * because they're keyed on `project_type` in the backend.
 */
export default function EditProjectTypeButton({ project, onSaved }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(project.project_type || "microwave");
  const [saving, setSaving] = useState(false);

  const isLegacy = !PROJECT_TYPES.find((t) => t.value === project.project_type);

  const save = async () => {
    if (value === project.project_type) {
      setOpen(false);
      return;
    }
    setSaving(true);
    try {
      const r = await api.patch(`/projects/${project.id}`, { project_type: value });
      toast.success(`Project type changed to ${projectTypeLabel(value)}`);
      setOpen(false);
      onSaved?.(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to change project type");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 text-slate-500 hover:text-blue-300 transition-colors ml-1.5 align-middle"
        title="Change project type"
        data-testid="edit-project-type-btn"
      >
        <Pencil className="w-3 h-3" />
      </button>

      <Dialog open={open} onOpenChange={(v) => !saving && setOpen(v)}>
        <DialogContent
          className="bg-slate-900 border-slate-800 text-slate-100"
          data-testid="edit-project-type-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display">Change Project Type</DialogTitle>
            <DialogDescription className="text-slate-400">
              Admin-only. Changes the project type and the photo template that
              will be applied on next resolve. Existing uploads keep their
              current category; no photos are deleted.
              {isLegacy && (
                <span className="block mt-2 text-amber-300">
                  Current type <span className="font-mono">{project.project_type}</span> is legacy —
                  switching to a supported type is recommended.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="py-2 space-y-2">
            <div className="text-[11px] tracking-widest uppercase text-slate-400 font-display font-bold">
              Project Type
            </div>
            <Select value={value} onValueChange={setValue}>
              <SelectTrigger
                className="bg-slate-950 border-slate-700 text-slate-100"
                data-testid="edit-project-type-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                {PROJECT_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value} data-testid={`project-type-option-${t.value}`}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {value !== project.project_type && (
              <div className="text-[11px] text-slate-500 pt-1">
                Changing from <span className="text-slate-300">{projectTypeLabel(project.project_type)}</span> →
                {" "}
                <span className="text-blue-300">{projectTypeLabel(value)}</span>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={saving}
              data-testid="edit-project-type-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={save}
              disabled={saving || value === project.project_type}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="edit-project-type-save"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
